import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthProfile } from 'types';

export interface AuthContextValue {
	user: string | undefined;
	profile: AuthProfile | null;
	signout: () => void;
	update: (updatedAuth: AuthProfile) => void;
	clear: () => void;
	reloadProfile: () => void;
}

export const AuthContext = React.createContext<AuthContextValue | null>(null);

export function useAuth() {
	return React.useContext(AuthContext);
}

export interface AuthProviderProps {
	children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
	const storedAuthString = localStorage.getItem('auth');

	let initialAuth: AuthProfile | null = null;
	if (storedAuthString) {
		try {
			initialAuth = JSON.parse(storedAuthString);
		} catch (err) {
			console.error('Could not parse stored auth information:', err);
		}
	}

	const navigate = useNavigate();

	const [profile, setProfile] = React.useState<AuthProfile | null>(initialAuth);

	const update = (updatedAuth: AuthProfile) => {
		setProfile(updatedAuth);
		localStorage.setItem('auth', JSON.stringify(updatedAuth));
	};

	const clear = () => {
		localStorage.removeItem('auth');
	};

	const signout = () => {
		navigate('/auth/logout');
	};

	const reloadProfile = () => {
		fetch('/api/auth/profile')
		.then(res => res.json())
		.then((json: { data?: AuthProfile }) => {
			if (json.data) setProfile(json.data);
		})
		.catch(err => {
			console.error('Failed to reload auth profile:', err);
			clear();
		});
	};

	const value: AuthContextValue = {
		user: profile?.user,
		profile,
		signout,
		update,
		clear,
		reloadProfile
	};

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthProvider;

